import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Spinner } from "reactstrap";
import {
  IoClose,
  IoPrintOutline,
  IoCreateOutline,
  IoShareSocialOutline,
  IoCardOutline,
  IoCopyOutline,
  IoTrashOutline,
  IoCheckmarkDoneCircleOutline
} from "react-icons/io5";
import { get, post } from "../../../../../utils/helpers/api";
import { toast } from "react-toastify";
import DefaultButton from "../../../../../components/Buttons/Default";
import "./styles.css";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import DOC_STYLES from "../../../utils/docStyles";
import ShareConfirmationModal from "../Modals/ShareConfirmation";
import { AWS_S3_URL, BASE_URL } from "../../../../../utils/constants";
import { printQuoteStatus } from "../../../utils/helpers";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../../../context/settings";
import { printSlug } from "../../../../../utils/helpers/text";
import RejectReasonModal from "../Modals/RejectReason";
import DefaultDrawer from "../../../../../components/Drawers/Default";

const PRIMARY = "#6C63FF";

function fmt(n) {
  return parseFloat(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function ViewDrawer({ quoteId, isOpen, onClose, onDelete, refreshQuotes }) {
  const { t } = useTranslation();
  const { formatDate } = useSettings();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [shareConfirmationModalOpen, setShareConfirmationModalOpen] = useState(false);
  const [quoteToReject, setQuoteToReject] = useState(null);

  const toggle = () => setDropdownOpen((prevState) => !prevState);

  const getQuote = async () => {
    if (!quoteId) return;

    try {
      const data = await get(`/sales/quotes/view/${quoteId}`);
      setQuote(data.quote ?? data.data ?? null);
      setCompanyData(data.company ?? null);
    } catch (error) {
      toast.error(t('sales.failedLoadQuote'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!quoteId) return;
    setLoading(true);
    setQuote(null);
    getQuote();
  }, [quoteId]);

  const currency = (quote?.currency ?? "usd").toUpperCase();

  const customerName = quote?.customer
    ? (quote.customer.company_name || `${quote.customer.firstname ?? ""} ${quote.customer.lastname ?? ""}`.trim())
    : "—";

  const salespersonName = quote?.salesperson
    ? `${quote.salesperson.firstname ?? ""} ${quote.salesperson.lastname ?? ""}`.trim()
    : null;

  const handlePrint   = () => window.print();
  const handleEdit    = () => { onClose(); navigate(`/sales/quotes/edit/${quote.id}`, { state: { quoteData: { ...quote } } }); };

  const markAsSent = async () => {
    const data = await post(`/sales/quotes/mark-as-sent/${quote.id}`);
    if (data?.success) {
      getQuote();
      refreshQuotes();
      toast.success(t('sales.quoteMarkedSent'));
    } else {
      toast.error(data?.message || t('sales.failedSentQuote'));
    }
  };

  const markAsRejected = async (reason) => {
    const data = await post(`/sales/quotes/mark-as-rejected/${quote.id}`, { reason });
    if (data?.success) {
      getQuote();
      refreshQuotes();
      toast.success(t('sales.quoteRejected'));
    } else {
      toast.error(data?.message || t('sales.failedRejectedQuote'));
    }
  };

  const convertToInvoice = async () => {
    const data = await post(`/sales/quotes/convert/${quote.id}`);
    if (data?.success) {
      navigate("/sales/invoices");
      toast.success(t('sales.quoteConverted'));
    } else {
      toast.error(data?.message || t('sales.failedConvertQuote'));
    }
  };

  const clone = async () => {
    const data = await post(`/sales/quotes/clone/${quote.id}`);
    if (data?.success) {
      toast.success(t('sales.quoteCloned'));
      refreshQuotes();
    } else {
      toast.error(data?.message || t('sales.failedCloneQuote'));
    }
  };

  const quoteLabel = quote ? `${t('sales.quoteHash')}${quote.quote_number || quote.id}` : t('sales.quote');

  const handleShare = async () => {
    const data = await post(`/sales/quotes/share/${quote.id}`);
    if (data?.success) {
      toast.success(t('sales.invoiceSharedCopied'));
      navigator.clipboard.writeText(BASE_URL + '/quote/' + quote?.id);
      refreshQuotes();
    } else {
      toast.error(data?.message || t('sales.failedShareQuote'));
    }
  }

  return (
    <DefaultDrawer 
      isOpen={isOpen} 
      size={720} 
      toggle={onClose}
    >
      {loading ?
        <Spinner color="primary" className="d-block mx-auto my-4" />
      :
        <div style={DOC_STYLES.wrap}>
          {/* ── Header ── */}
          <div style={DOC_STYLES.header}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                style={{ background: "none", border: "none", cursor: "pointer", color: "#888", padding: 4, borderRadius: 6, display: "flex", alignItems: "center" }}
                onClick={onClose}
                onMouseEnter={e => { e.currentTarget.style.background = "#f0f0f0"; e.currentTarget.style.color = "#333"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "#888"; }}
              >
                <IoClose size={20} />
              </button>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#1d1d1f" }}>{quoteLabel}</span>
              {quote && 
                printQuoteStatus(quote.status)
              }
            </div>
          </div>

          {/* ── Action Bar ── */}
          <div style={DOC_STYLES.actionBar}>
            <DefaultButton className="btn-action" size="sm" onClick={handleEdit} disabled={!quote} color="light" outline>
              <IoCreateOutline size={15} />
              {t('common.edit')}
            </DefaultButton>
            <DefaultButton className="btn-action" size="sm" onClick={() => setShareConfirmationModalOpen(true)} disabled={!quote} color="light" outline>
              <IoShareSocialOutline size={15} />
              {t('common.share')}
            </DefaultButton>
            <DefaultButton className="btn-action" size="sm" onClick={handlePrint} disabled={!quote} color="light" outline>
              <IoPrintOutline size={15} />
              {t('common.download')}
            </DefaultButton>
            {quote?.status !== "converted" && (
              <DefaultButton className="btn-action" size="sm" onClick={convertToInvoice} disabled={!quote} color="light" outline>
                <IoCardOutline size={15} />
                {t('sales.converQuoteToInvoice')}
              </DefaultButton>
            )}
            <Dropdown isOpen={dropdownOpen} toggle={toggle} direction="down" size="sm">
              <DropdownToggle outline color="light" className="btn-action">
                {t('common.options')} <PiDotsThreeOutlineVerticalFill />
              </DropdownToggle>
              <DropdownMenu end>
                {quote?.status === 'draft' && (
                  <DropdownItem onClick={markAsSent}>
                    <IoCheckmarkDoneCircleOutline size={16} /> {t('common.sent')}
                  </DropdownItem>
                )}

                {quote?.status !== 'rejected' && (
                  <DropdownItem onClick={() => setQuoteToReject(quote)}>
                    <IoCheckmarkDoneCircleOutline size={16} /> {t('common.rejected')}
                  </DropdownItem>
                )}
                <DropdownItem onClick={clone} disabled={!quote}>
                  <IoCopyOutline size={16} /> {t('sales.clone')}
                </DropdownItem>
                <DropdownItem divider />
                <DropdownItem className="text-danger" onClick={onDelete} disabled={!quote}>
                  <IoTrashOutline size={16} /> {t('common.delete')}
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>

          {/* ── Scrollable document area ── */}
          <div style={DOC_STYLES.scroll}>
            {!loading && quote && (
              <div id="quote-print-area" style={DOC_STYLES.paper}>

                {/* Company + Quote Title */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 36 }}>
                  <div>
                    <img
                      src={companyData?.logo_path ? AWS_S3_URL + companyData.logo_path : require('../../../../../assets/images/brand.svg').default}
                      height={38}
                      style={{ marginBottom: 10 }}
                      alt={t('common.logo')}
                    />
                    <div style={{ fontWeight: 800, fontSize: 18, color: "#1d1d1f", lineHeight: 1.2 }}>
                      { companyData?.legal_name || t('common.companyNamePlaceholder') }
                    </div>
                    <div style={{ fontSize: 12, color: "#777", marginTop: 6, lineHeight: 1.8 }}>
                      { companyData?.street_address || t('sales.addressPlaceholder') }<br />
                      { companyData?.city || t('common.city') } { companyData?.state || t('common.state') } { companyData?.postal_code || t('common.postalCode') }<br />
                      { printSlug(companyData?.country) || t('common.country') }<br />
                      { companyData?.email || t('common.emailPlaceholder') }
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 800, fontSize: 30, color: PRIMARY, letterSpacing: 3, lineHeight: 1, textTransform: "uppercase" }}>
                      {t('sales.quote')}
                    </div>
                    <div style={{ color: "#888", fontSize: 14, marginTop: 6, fontWeight: 600 }}>
                      #{quote.quote_number || quote.id}
                    </div>
                  </div>
                </div>

                <hr style={DOC_STYLES.divider} />

                {/* Bill To + Meta */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
                  {quote?.quote_for === 'lead' ? 
                    <div>
                      <div style={DOC_STYLES.label}>{t('sales.billTo')}</div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: "#1d1d1f" }}>{quote?.lead?.name}</div>
                      {quote?.lead && 
                        <div style={{ fontSize: 12, color: "#777", marginTop: 6, lineHeight: 1.8 }}>
                          { quote?.lead?.street }<br />
                          { quote?.lead?.city }, { quote?.lead?.state } { quote?.lead?.zip_code }<br />
                          { printSlug(quote?.lead?.country) }
                        </div>
                      }
                    </div>
                  :
                    <div>
                      <div style={DOC_STYLES.label}>{t('sales.billTo')}</div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: "#1d1d1f" }}>{customerName}</div>
                      {quote?.customer?.addresses?.length > 0 && 
                        <div style={{ fontSize: 12, color: "#777", marginTop: 6, lineHeight: 1.8 }}>
                          { quote.customer.addresses[0].street }<br />
                          { quote.customer.addresses[0].city }, { quote.customer.addresses[0].state } { quote.customer.addresses[0].postal_code }<br />
                          { printSlug(quote.customer.addresses[0].country) }
                        </div>
                      }
                    </div>
                  }
                  <div>
                    <table style={DOC_STYLES.metaTable}>
                      <tbody>
                        <tr>
                          <td style={DOC_STYLES.metaThTd}>{t('sales.quoteDate')}</td>
                          <td style={DOC_STYLES.metaTd}>{formatDate(quote.quote_date) || "—"}</td>
                        </tr>
                        <tr>
                          <td style={DOC_STYLES.metaThTd}>{t('sales.expiry')}</td>
                          <td style={DOC_STYLES.metaTd}>{formatDate(quote.expiry_date) || "—"}</td>
                        </tr>
                        {salespersonName && (
                          <tr>
                            <td style={DOC_STYLES.metaThTd}>{t('sales.salesperson')}</td>
                            <td style={DOC_STYLES.metaTd}>{salespersonName}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {quote.subject && (
                  <div style={{ marginBottom: 24, fontSize: 13, background: "#f9f9ff", border: "1px solid #ede9ff", borderRadius: 7, padding: "10px 14px", color: "#444" }}>
                    <strong style={{ color: PRIMARY }}>{t('sales.re')}</strong>&nbsp;{quote.subject}
                  </div>
                )}

                {/* Items table */}
                <table style={DOC_STYLES.itemsTable}>
                  <thead>
                    <tr>
                      <th style={{ ...DOC_STYLES.th, width: 36, borderRadius: "6px 0 0 0" }}>#</th>
                      <th style={DOC_STYLES.th}>{t('common.itemDescription')}</th>
                      <th style={{ ...DOC_STYLES.thRight, width: 60 }}>{t('common.qty')}</th>
                      <th style={{ ...DOC_STYLES.thRight, width: 100 }}>{t('common.unitPrice')}</th>
                      <th style={{ ...DOC_STYLES.thRight, width: 100 }}>{t('sales.discount')}</th>
                      <th style={{ ...DOC_STYLES.thRight, width: 100 }}>{t('sales.taxPercent')}</th>
                      <th style={{ ...DOC_STYLES.thRight, width: 100, borderRadius: "0 6px 0 0" }}>{t('common.total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quote.items.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ ...DOC_STYLES.td, textAlign: "center", color: "#aaa", padding: "32px 0" }}>{t('common.noItemsAdded')}</td>
                      </tr>
                    ) : (
                      quote.items.map((item, i) => (
                        <tr key={item.id ?? i} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                          <td style={DOC_STYLES.td}>{i + 1}</td>
                          <td style={DOC_STYLES.td}>
                            <span style={{ fontWeight: 600 }}>{item.product_name || "—"}</span>
                            {item.description && (
                              <div style={{ color: "#888", fontSize: 12, marginTop: 2 }}>{item.description}</div>
                            )}
                          </td>
                          <td style={DOC_STYLES.tdRight}>{item.quantity}</td>
                          <td style={DOC_STYLES.tdRight}>{fmt(item.unit_price)}</td>
                          <td style={DOC_STYLES.tdRight}>{fmt(item.discount_amount)}%</td>
                          <td style={DOC_STYLES.tdRight}>{fmt(item.tax_rate)}%</td>
                          <td style={DOC_STYLES.tdRight}>{fmt(item.total_price)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                {/* Totals */}
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
                  <div style={DOC_STYLES.totalsBox}>
                    <div style={DOC_STYLES.totalsLine}>
                      <span>{t('common.subTotal')}</span>
                      <span style={{ fontWeight: 500 }}>{fmt(quote?.sub_total ?? 0)} {currency}</span>
                    </div>
                    {quote.discount_amount > 0 && (
                      <div style={DOC_STYLES.totalsLine}>
                        <span>{t('sales.discount')}</span>
                        <span style={{ fontWeight: 500, color: "red" }}>- {fmt(quote?.discount_amount ?? 0)} {currency}</span>
                      </div>
                    )}
                    {quote.tax_amount > 0 && (
                      <div style={DOC_STYLES.totalsLine}>
                        <span>{t('sales.taxWithRate', { rate: quote.tax_rate ?? 0 })}</span>
                        <span style={{ fontWeight: 500 }}>{fmt(quote?.tax_amount ?? 0)} {currency}</span>
                      </div>
                    )}
                    <div style={DOC_STYLES.totalsBold}>
                      <span>{t('common.total')}</span>
                      <span style={{ color: PRIMARY }}>{fmt(quote?.total_amount ?? 0)} {currency}</span>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {quote.notes && (
                  <div style={{ marginTop: 36, paddingTop: 20, borderTop: "1px solid #eee" }}>
                    <div style={DOC_STYLES.label}>{t('sales.notes')}</div>
                    <p style={{ fontSize: 13, color: "#555", whiteSpace: "pre-wrap", margin: 0, lineHeight: 1.7 }}>
                      {quote.notes}
                    </p>
                  </div>
                )}

              </div>
            )}
          </div>
        </div>
      }

      <ShareConfirmationModal 
        isOpen={shareConfirmationModalOpen}
        toggle={() => setShareConfirmationModalOpen(!shareConfirmationModalOpen)}
        onConfirm={() => handleShare()}
      />

      <RejectReasonModal 
        open={!!quoteToReject}
        onClose={() => setQuoteToReject(null)}
        onSubmit={markAsRejected}
        quoteData={quoteToReject}
      />
    </DefaultDrawer>
  );
}
